import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import type { UserSessionsHistogramEntry } from '../../api/types';

interface Props {
  entries: UserSessionsHistogramEntry[];
  loading?: boolean;
}

const BUCKET_LABELS: Record<UserSessionsHistogramEntry['bucket'], string> = {
  '1': 'One-shot (1)',
  '2-4': 'Light (2–4)',
  '5-20': 'Regular (5–20)',
  '21+': 'Heavy (21+)',
};

const BUCKET_COLORS: Record<UserSessionsHistogramEntry['bucket'], string> = {
  '1': '#94a3b8', // slate 400
  '2-4': '#6366f1', // indigo 500
  '5-20': '#10b981', // emerald 500
  '21+': '#f59e0b', // amber 500
};

export function SessionsPerUserHistogram({ entries, loading }: Props): JSX.Element {
  const total = entries.reduce((s, e) => s + e.users, 0);
  const option: EChartsOption = {
    grid: { left: 48, right: 24, top: 16, bottom: 56 },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const items = Array.isArray(params) ? params : [params];
        const first = items[0];
        if (!first) return '';
        const idx = first.dataIndex as number;
        const entry = entries[idx];
        if (!entry) return '';
        const pct = total > 0 ? ((entry.users / total) * 100).toFixed(1) : '0';
        return `<strong>${BUCKET_LABELS[entry.bucket]}</strong><br/>${entry.users.toLocaleString('en-US')} users (${pct}%)`;
      },
    },
    xAxis: {
      type: 'category',
      data: entries.map((e) => BUCKET_LABELS[e.bucket]),
      name: 'Sessions per user',
      nameLocation: 'middle',
      nameGap: 32,
      axisLabel: { fontSize: 12 },
    },
    yAxis: {
      type: 'value',
      name: 'Users',
      nameLocation: 'middle',
      nameGap: 36,
      minInterval: 1,
    },
    series: [
      {
        type: 'bar',
        barMaxWidth: 56,
        itemStyle: { borderRadius: [4, 4, 0, 0] },
        label: {
          show: true,
          position: 'top',
          formatter: (p) => (p.value as number).toLocaleString('en-US'),
          fontSize: 12,
        },
        data: entries.map((e) => ({
          value: e.users,
          itemStyle: { color: BUCKET_COLORS[e.bucket] },
        })),
      },
    ],
  };
  return (
    <ReactECharts
      option={option}
      style={{ height: 280, width: '100%' }}
      showLoading={loading}
      notMerge
      lazyUpdate
    />
  );
}
